import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
  InputAdornment
} from '@mui/material';
import AppDialog, { AppDialogTitle, AppDialogContent, AppDialogActions } from '../layout/AppDialog';
import { updateUserDetails, getCurrentUser } from '../../services/api';
import { formatKenyanPhone, isValidKenyanPhone } from '../../utils/phoneUtils';

const EditUserDetailsModal = ({ open, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);
  const [phoneError, setPhoneError] = useState('');
  const [formValid, setFormValid] = useState(true);

  // Reset when modal closes
  const handleClose = () => {
    setError('');
    setSuccess('');
    setPhoneError('');
    onClose();
  };

  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!open) return;
      
      try {
        setInitialLoading(true);
        const response = await getCurrentUser();
        const userData = response.data;
        
        // Format phone if exists
        const formattedPhone = userData.phone ? formatKenyanPhone(userData.phone) : '+254';
        
        setFormData({
          name: userData.name || '',
          email: userData.email || '',
          phone: formattedPhone
        });
      } catch (err) {
        setError('Failed to load user details');
      } finally {
        setInitialLoading(false);
      }
    };

    if (open) {
      fetchUserDetails();
    }
  }, [open]);

  // Validate form whenever data changes
  useEffect(() => {
    const validateForm = () => {
      // Phone validation
      const phoneValid = !formData.phone || isValidKenyanPhone(formData.phone);
      setPhoneError(phoneValid ? '' : 'Phone must be in format +254XXXXXXXXX');
      
      // Email validation
      const emailValid = !formData.email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
      
      setFormValid(phoneValid && emailValid);
    };
    
    validateForm();
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      // Format phone number when it changes
      const formattedPhone = formatKenyanPhone(value);
      setFormData(prev => ({
        ...prev,
        [name]: formattedPhone
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formValid) return;
    
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await updateUserDetails(formData);
      setSuccess('Profile updated successfully');
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <AppDialog open={open} onClose={handleClose}>
        <AppDialogContent dividers={false}>
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        </AppDialogContent>
      </AppDialog>
    );
  }

  return (
    <AppDialog open={open} onClose={handleClose}>
      <AppDialogTitle onClose={handleClose} subtitle="Update your profile information">
        Edit Profile
      </AppDialogTitle>
      <form onSubmit={handleSubmit}>
        <AppDialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          
          <TextField
            fullWidth
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Phone (optional)"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            error={!!phoneError}
            helperText={phoneError || "Format: +254XXXXXXXXX"}
            margin="normal"
            InputProps={{
              startAdornment: formData.phone && !formData.phone.startsWith('+') ? (
                <InputAdornment position="start">+</InputAdornment>
              ) : null
            }}
          />
        </AppDialogContent>
        <AppDialogActions>
          <Button onClick={handleClose} variant="outlined" color="inherit">Cancel</Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading || !formValid}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </AppDialogActions>
      </form>
    </AppDialog>
  );
};

export default EditUserDetailsModal; 